export type LessonAiProgressPhase = {
  readonly id: string;
  readonly label: string;
  readonly detail: string;
  /** Elapsed ms before this phase becomes active (staged for UX, not literal backend steps). */
  readonly minMs: number;
};

export type LessonAiProgressUpdate = {
  readonly phaseIndex: number;
  readonly phase: LessonAiProgressPhase;
  readonly phases: readonly LessonAiProgressPhase[];
  readonly elapsedMs: number;
};

export const LESSON_AI_GENERATE_PHASES: readonly LessonAiProgressPhase[] = [
  {
    id: "understand",
    label: "Understanding your prompt",
    detail: "Reading what you asked for and how it should work in a classroom.",
    minMs: 0,
  },
  {
    id: "outline",
    label: "Planning the lesson structure",
    detail: "Choosing concepts, pacing, and what students should accomplish by the end.",
    minMs: 3500,
  },
  {
    id: "starter",
    label: "Writing starter code",
    detail: "Drafting main.py with short placeholder comments and the right amount of scaffolding.",
    minMs: 8000,
  },
  {
    id: "tour",
    label: "Building the guided tour",
    detail: "Creating step-by-step instructions students follow in the IDE.",
    minMs: 15000,
  },
  {
    id: "tests",
    label: "Generating validation tests",
    detail: "Writing automated checks that confirm student work meets your goals.",
    minMs: 22000,
  },
  {
    id: "polish",
    label: "Polishing the draft",
    detail: "Tightening wording and making sure the pieces fit together.",
    minMs: 30000,
  },
];

export const LESSON_AI_REFINE_PHASES: readonly LessonAiProgressPhase[] = [
  {
    id: "review",
    label: "Reviewing your lesson",
    detail: "Looking at the current title, code, tour, and checks you already have.",
    minMs: 0,
  },
  {
    id: "interpret",
    label: "Interpreting your follow-up",
    detail: "Deciding what to change and what to keep from the existing draft.",
    minMs: 3000,
  },
  {
    id: "apply",
    label: "Applying your changes",
    detail: "Updating starter code and instructions to match your request.",
    minMs: 7500,
  },
  {
    id: "checks",
    label: "Adjusting validation tests",
    detail: "Refreshing automated checks so they still match the lesson.",
    minMs: 14000,
  },
  {
    id: "polish",
    label: "Polishing the updated draft",
    detail: "Making sure the revised lesson still reads clearly for students.",
    minMs: 21000,
  },
];

export const COURSE_TEMPLATE_AI_GENERATE_PHASES: readonly LessonAiProgressPhase[] = [
  {
    id: "understand",
    label: "Understanding your course vision",
    detail: "Reading what kind of sequence you want and who it is for.",
    minMs: 0,
  },
  {
    id: "catalog",
    label: "Reviewing your lesson library",
    detail: "Matching your existing lessons to the goals in your prompt.",
    minMs: 2500,
  },
  {
    id: "sequence",
    label: "Designing the lesson path",
    detail: "Choosing which lessons belong and arranging them for a smooth ramp.",
    minMs: 6000,
  },
  {
    id: "narrative",
    label: "Writing the course overview",
    detail: "Drafting a teacher-facing title and description for the template.",
    minMs: 10000,
  },
  {
    id: "polish",
    label: "Polishing the blueprint",
    detail: "Double-checking order and trimming anything that does not fit.",
    minMs: 15000,
  },
];

export const COURSE_TEMPLATE_AI_REFINE_PHASES: readonly LessonAiProgressPhase[] = [
  {
    id: "review",
    label: "Reviewing your draft sequence",
    detail: "Looking at the current title, description, and lesson order.",
    minMs: 0,
  },
  {
    id: "interpret",
    label: "Interpreting your follow-up",
    detail: "Deciding which lessons to swap, add, remove, or reorder.",
    minMs: 2500,
  },
  {
    id: "apply",
    label: "Updating the course path",
    detail: "Applying your changes using lessons from your library.",
    minMs: 6500,
  },
  {
    id: "polish",
    label: "Polishing the revised blueprint",
    detail: "Making sure the sequence still reads clearly for you and your students.",
    minMs: 12000,
  },
];

export const QUIZ_AI_GENERATE_PHASES: readonly LessonAiProgressPhase[] = [
  {
    id: "context",
    label: "Reading preceding lessons",
    detail: "Finding the lessons before this spot in your course plan.",
    minMs: 0,
  },
  {
    id: "concepts",
    label: "Reviewing lesson content",
    detail: "Looking at starter code, guided tour steps, and learning goals.",
    minMs: 4000,
  },
  {
    id: "topics",
    label: "Choosing what to assess",
    detail: "Picking concepts students should understand from the recent lessons.",
    minMs: 9000,
  },
  {
    id: "questions",
    label: "Drafting questions",
    detail: "Writing clear prompts with four answer choices each.",
    minMs: 15000,
  },
  {
    id: "distractors",
    label: "Crafting distractors",
    detail: "Adding plausible wrong answers that surface common misconceptions.",
    minMs: 21000,
  },
  {
    id: "polish",
    label: "Polishing the quiz",
    detail: "Tightening wording and checking the question pool size.",
    minMs: 27000,
  },
];

export const PRESENTATION_AI_GENERATE_PHASES: readonly LessonAiProgressPhase[] = [
  {
    id: "place",
    label: "Finding this session",
    detail: "Seeing where this presentation sits in the course plan.",
    minMs: 0,
  },
  {
    id: "prior",
    label: "Reviewing earlier decks",
    detail: "Checking what previous presentations and exercises already covered.",
    minMs: 4000,
  },
  {
    id: "focus",
    label: "Reading the next exercise",
    detail: "Looking at starter code, tour steps, and what is new this session.",
    minMs: 9000,
  },
  {
    id: "outline",
    label: "Outlining the deck",
    detail: "Choosing which new ideas to introduce and which to skip.",
    minMs: 15000,
  },
  {
    id: "slides",
    label: "Writing slides",
    detail: "Drafting titles, talking points, and short Python examples.",
    minMs: 21000,
  },
  {
    id: "polish",
    label: "Polishing the deck",
    detail: "Matching the exercise theme and tightening wording.",
    minMs: 27000,
  },
];

export function phasesForLessonAiMode(
  mode: "generate" | "refine" | "template-generate" | "template-refine" | "quiz-generate" | "presentation-generate"
): readonly LessonAiProgressPhase[] {
  if (mode === "presentation-generate") return PRESENTATION_AI_GENERATE_PHASES;
  if (mode === "quiz-generate") return QUIZ_AI_GENERATE_PHASES;
  if (mode === "template-generate") return COURSE_TEMPLATE_AI_GENERATE_PHASES;
  if (mode === "template-refine") return COURSE_TEMPLATE_AI_REFINE_PHASES;
  return mode === "refine" ? LESSON_AI_REFINE_PHASES : LESSON_AI_GENERATE_PHASES;
}

export function lessonAiPhaseAtElapsed(
  phases: readonly LessonAiProgressPhase[],
  elapsedMs: number
): LessonAiProgressUpdate {
  let phaseIndex = 0;
  for (let i = phases.length - 1; i >= 0; i--) {
    if (elapsedMs >= phases[i]!.minMs) {
      phaseIndex = i;
      break;
    }
  }
  return {
    phaseIndex,
    phase: phases[phaseIndex]!,
    phases,
    elapsedMs,
  };
}

/** Advance staged progress labels on a timer while the async Bedrock job runs. */
export function startLessonAiProgressClock(
  mode: "generate" | "refine" | "template-generate" | "template-refine" | "quiz-generate" | "presentation-generate",
  onProgress: (update: LessonAiProgressUpdate) => void
): () => void {
  const phases = phasesForLessonAiMode(mode);
  const start = Date.now();
  let lastIndex = -1;

  const tick = () => {
    const update = lessonAiPhaseAtElapsed(phases, Date.now() - start);
    if (update.phaseIndex !== lastIndex) {
      lastIndex = update.phaseIndex;
      onProgress(update);
    }
  };

  tick();
  const id = window.setInterval(tick, 700);
  return () => window.clearInterval(id);
}
